import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import AssignedTagsList from "components/assigned-tags";
import ProductPictureIcon from "components/product-picture-icon";
import { TagTypes } from "constants/tags";
import { InventoryProduct } from "services/types/inventory-products.type";

import { getInventoryPillMeta } from "./inventory-product-field.util";
import InventoryProductPills from "./inventory-product-pills.component";

const BRAND_TEAL = "#0F6E56";

type InventoryProductListItemProps = {
  props: object;
  option: InventoryProduct;
  isNew?: boolean;
  showAssignedTags?: boolean;
  // Label each row with the clinic that holds the unit (SPA-6232). Only set
  // when the picker's scope spans more than one clinic.
  showClinic?: boolean;
  currentClinicId?: number;
};

export default function InventoryProductListItem({
  props,
  option,
  isNew = false,
  showAssignedTags = false,
  showClinic = false,
  currentClinicId
}: InventoryProductListItemProps) {
  const assignedTags = option.assigned_tags ?? [];
  const meta = {
    ...getInventoryPillMeta(option),
    clinic:
      showClinic && option.clinic?.name
        ? {
            name: option.clinic.name,
            isCurrent: option.clinic.id === currentClinicId
          }
        : undefined
  };

  return (
    <ListItem
      {...props}
      key={`${option.id}-${option.serial_number}`}
      alignItems="flex-start"
      sx={{
        px: "14px",
        py: "10px",
        borderLeft: "3px solid transparent",
        borderBottom: (theme) => `0.5px solid ${theme.palette.divider}`,
        transition: "background-color 120ms ease, border-color 120ms ease",
        "&:hover": {
          backgroundColor: (theme) => theme.palette.action.hover,
          borderLeftColor: BRAND_TEAL
        }
      }}
      data-testid="inventroy-product-item"
    >
      <Stack
        spacing={0}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
      >
        <Box>
          <Stack direction="row" alignItems="center">
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: isNew ? 700 : 500,
                lineHeight: 1.4,
                color: "text.primary"
              }}
            >
              {option.product.display_name.trim()}
            </Typography>
            <ProductPictureIcon
              productId={option.product.id}
              hasPicture={option.product.has_picture}
            />
          </Stack>

          {!isNew && <InventoryProductPills meta={meta} />}

          {!isNew && showAssignedTags && Boolean(assignedTags.length) && (
            <AssignedTagsList
              tagType={TagTypes.INVENTORY_PRODUCT}
              tags={assignedTags}
              dense
            />
          )}
        </Box>

        {isNew && (
          <ListItemIcon sx={{ minWidth: "auto" }}>
            <AddIcon />
          </ListItemIcon>
        )}
      </Stack>
    </ListItem>
  );
}
